import { useCallback } from 'react';
import Pusher from 'pusher-js';
import config from 'config/config';
import { trpc } from 'util/trpc';
import { useRoundtableAuth0 } from '../auth/hooks/useRoundtableAuth0';
import { addDatadogError } from 'util/datadog';

/**
 * cached client in memory, storing as a promise - so we don't dog-pile during initial render
 */
let clientPromise: Promise<Pusher> | null;

/**
 * Guards the one-time attachment of document/window liveness listeners so
 * they are never registered more than once per page lifetime even if the
 * module is evaluated multiple times during hot-reloads.
 */
let livenessListenersAttached = false;

// Backoff schedule for retrying a single failed channel subscription.
// Total wall time across all retries is ~4 minutes — long enough to outlast
// transient auth/network blips, short enough that a permanently-broken
// channel surfaces as a bounded error.
const SUBSCRIBE_RETRY_DELAYS_MS = [
  2_000, 5_000, 10_000, 30_000, 60_000, 60_000, 60_000,
] as const;

// Upper bound on how long we wait for a retry attempt to settle (i.e. fire
// either pusher:subscription_succeeded or pusher:subscription_error). Without
// this, a hung auth fetch or dropped WebSocket mid-subscribe leaves the
// per-attempt promise pending forever — which keeps the channel's slot in
// `channelsBeingRetried` reserved for the page lifetime, blocking any future
// retry chain for that channel.
const SUBSCRIBE_ATTEMPT_TIMEOUT_MS = 30_000;

/**
 * True only when the named channel exists in the client AND is in the
 * subscribed state. `client.channels.all()` returns channels in any state
 * (pending_subscription, failed, subscribed) — relying on presence alone
 * would falsely report a stuck channel as recovered.
 */
function isChannelActivelySubscribed(client: Pusher, channelName: string) {
  return client.channels
    .all()
    .some((c) => c.name === channelName && c.subscribed === true);
}

/**
 * Channels currently in a retry loop. Prevents duplicate retry chains when
 * subscribeToChannels is called more than once (e.g. reconnect re-subscribe).
 * Module-scoped because the underlying Pusher client is also a singleton.
 */
const channelsBeingRetried = new Set<string>();

/**
 * Bumped on every disconnect. In-flight retry IIFEs capture this value at
 * their start and bail per-iteration if it has changed — preventing
 * client.subscribe() calls against a Pusher instance that was disconnect()-ed
 * during a retry sleep (which would otherwise fire a ghost subscription on
 * the dead socket after logout/re-login).
 */
let clientGeneration = 0;

/**
 * Wipe in-flight retry tracking. Called by useDisconnectPushersChannels so
 * the next session isn't blocked by orphaned retry loops from the old client.
 */
export function clearChannelsBeingRetried() {
  channelsBeingRetried.clear();
}

/**
 * 401/403/404 from /pusher/auth indicate a permanent failure for this
 * (user, channel) pair — token rejected, no group access, or channel doesn't
 * exist on the auth endpoint. Retrying won't change the answer. Anything
 * else (5xx, 429, network errors) is treated as transient and retried.
 *
 * The web Pusher SDK surfaces subscription errors as
 * `{ type: 'AuthError', error: 'Received HTTP 403 ...' }` or similar.
 * We also match the string thrown by our customHandler below:
 * `Pusher auth failed: <status> ...`
 */
function isPermanentSubscribeFailure(error: unknown): boolean {
  return /Pusher auth failed: (401|403|404)\b/.test(String(error));
}

// Cleanup for the active liveness listeners. Set when attachLivenessListeners
// registers handlers, called from useDisconnectPushersChannels. Without this,
// each logout/re-login cycle leaves the previous session's handlers on
// document/window — and they continue calling .connect() on the disconnected
// client they captured in closure.
let stopLivenessListeners: (() => void) | null = null;

/**
 * Attach document visibility + window online listeners exactly once.
 * On either event, if the Pusher connection is not already connected or
 * actively connecting, call connect() to heal the stale socket.
 *
 * This is the primary fix for the auction countdown bug: a tab that
 * was backgrounded and re-opened had a stale WS that never healed.
 */
function attachLivenessListeners(client: Pusher) {
  if (livenessListenersAttached) return;
  livenessListenersAttached = true;

  const reconnectIfStale = () => {
    const state = client.connection.state;
    if (state !== 'connected' && state !== 'connecting') {
      console.debug(
        `[Pusher] Reconnecting stale connection (state=${state})...`
      );
      try {
        client.connect();
      } catch (error) {
        console.error(`[Pusher] Reconnect failed: ${String(error)}`);
        addDatadogError(error, { context: 'pusher-reconnect' });
      }
    }
  };

  const onVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      reconnectIfStale();
    }
  };

  document.addEventListener('visibilitychange', onVisibilityChange);
  window.addEventListener('online', reconnectIfStale);

  stopLivenessListeners = () => {
    document.removeEventListener('visibilitychange', onVisibilityChange);
    window.removeEventListener('online', reconnectIfStale);
    stopLivenessListeners = null;
  };
}

/**
 * Start the 10-minute heartbeat that heals any non-CONNECTED state.
 * Returns a cleanup handle. Call it once after the client is created.
 *
 * Mirrors mobile's heartbeat for SDK issue #129 — events silently stop
 * after extended sessions because the WS state goes stale without a
 * state-change event surfacing it. CONNECTING is excluded to avoid
 * racing the SDK's own auto-reconnect.
 */
function startLivenessHeartbeat(client: Pusher): () => void {
  const interval = setInterval(() => {
    const state = client.connection.state;
    if (state !== 'connected' && state !== 'connecting') {
      console.debug(`[Pusher] Heartbeat: state=${state}, reconnecting...`);
      try {
        client.connect();
      } catch (error) {
        console.error(`[Pusher] Heartbeat reconnect failed: ${String(error)}`);
        addDatadogError(error, { context: 'pusher-heartbeat-reconnect' });
      }
    }
  }, 1_000 * 60 * 10);

  return () => clearInterval(interval);
}

// Module-scoped heartbeat cleanup — holds the teardown for the single
// running heartbeat so it can be stopped on explicit disconnect.
let stopHeartbeat: (() => void) | null = null;

function makeClient(userAccessToken: string): Pusher {
  const client = new Pusher(config.pusher.channelsKey, {
    cluster: config.pusher.channelsCluster,
    // see [here](https://pusher.com/docs/channels/server_api/authenticating-users/#using-jsonp-in-pusher-js)
    userAuthentication: {
      transport: 'ajax',
      endpoint: `${config.baseUrl}/pusher/user-auth`,
      headers: {
        authorization: `Bearer ${userAccessToken}`,
      },
    },
    channelAuthorization: {
      transport: 'ajax',
      endpoint: `${config.baseUrl}/pusher/auth`,
      headers: {
        authorization: `Bearer ${userAccessToken}`,
      },
      // Validate that the /pusher/auth response has the expected shape before
      // passing it to the SDK. The built-in transport path does no validation;
      // a server-side error JSON (missing `auth` field) would silently
      // poison the channel and leave it permanently unsubscribed.
      customHandler: async ({ channelName, socketId }, callback) => {
        try {
          const response = await fetch(`${config.baseUrl}/pusher/auth`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${userAccessToken}`,
            },
            body: JSON.stringify({
              socket_id: socketId,
              channel_name: channelName,
            }),
          });

          if (!response.ok) {
            const body = await response.text().catch(() => '(unreadable)');
            const msg = `Pusher auth failed: ${response.status} ${
              response.statusText
            } - ${body.slice(0, 200)}`;
            console.error(`[Pusher:auth] ${msg}`);
            addDatadogError(new Error(msg), {
              context: 'pusher-channel-auth',
              channel: channelName,
              status: response.status,
            });
            callback(new Error(msg), null);
            return;
          }

          const authorization: unknown = await response.json();

          // Validate shape — the SDK silently misbehaves if `auth` is absent.
          if (
            authorization == null ||
            typeof authorization !== 'object' ||
            typeof (authorization as { auth?: unknown }).auth !== 'string'
          ) {
            const msg = `Pusher auth response missing 'auth' field: ${JSON.stringify(
              authorization
            ).slice(0, 200)}`;
            console.error(`[Pusher:auth] ${msg}`);
            addDatadogError(new Error(msg), {
              context: 'pusher-channel-auth-shape',
              channel: channelName,
            });
            callback(new Error(msg), null);
            return;
          }

          callback(null, authorization as { auth: string });
        } catch (error) {
          const msg = `Pusher auth request threw: ${String(error)}`;
          console.error(`[Pusher:auth] ${msg}`);
          addDatadogError(error, {
            context: 'pusher-channel-auth-exception',
            channel: channelName,
          });
          callback(error instanceof Error ? error : new Error(msg), null);
        }
      },
    },
  });

  client.connection.bind(
    'error',
    function (err: Error & { data?: { code?: number } }) {
      if (err.data?.code === 4004) {
        console.error('Pusher channels client is over the limit');
        addDatadogError(err, { context: 'pusher-connection-over-limit' });
      }
    }
  );

  // Attach one-time browser liveness listeners and start the heartbeat.
  attachLivenessListeners(client);
  stopHeartbeat?.();
  stopHeartbeat = startLivenessHeartbeat(client);

  return client;
}

function subscribeOneWithRetry(client: Pusher, channel: string) {
  if (isChannelActivelySubscribed(client, channel)) {
    return;
  }

  const pusherChannel = client.subscribe(channel);

  // Listen for subscription errors and drive per-channel exponential backoff.
  pusherChannel.bind(
    'pusher:subscription_error',
    function onSubscriptionError(errorData: unknown) {
      pusherChannel.unbind('pusher:subscription_error', onSubscriptionError);

      const errorMsg =
        errorData != null && typeof errorData === 'object'
          ? JSON.stringify(errorData)
          : String(errorData);

      if (isPermanentSubscribeFailure(errorMsg)) {
        console.error(
          `[Pusher] Permanent failure subscribing to channel=${channel}; not retrying`,
          errorData
        );
        addDatadogError(new Error(errorMsg), {
          context: 'pusher-subscribe-permanent-failure',
          channel,
        });
        return;
      }

      if (channelsBeingRetried.has(channel)) return;
      channelsBeingRetried.add(channel);

      // Snapshot the current client generation. If a logout/disconnect
      // bumps it during a retry sleep, we abort instead of calling
      // client.subscribe() against a disconnect()-ed instance.
      const generationAtStart = clientGeneration;

      (async () => {
        try {
          for (
            let attempt = 0;
            attempt < SUBSCRIBE_RETRY_DELAYS_MS.length;
            attempt++
          ) {
            const delay = SUBSCRIBE_RETRY_DELAYS_MS[attempt]!;
            await new Promise((resolve) => setTimeout(resolve, delay));

            if (generationAtStart !== clientGeneration) {
              // Client was disconnected while we slept. Don't touch the
              // dead client; the next session will re-bootstrap from
              // scratch.
              return;
            }

            if (isChannelActivelySubscribed(client, channel)) {
              console.debug(
                `[Pusher] channel=${channel} recovered concurrently after ${attempt} retries`
              );
              return;
            }

            const retryChannel = client.subscribe(channel);

            // Await a settled subscription outcome for this attempt.
            // Resolves true on success, rejects with the error string on
            // permanent failure (so the outer try/catch can bail), resolves
            // false on transient failure or timeout (so the loop continues).
            const succeeded = await new Promise<boolean>((resolve, reject) => {
              let settled = false;
              const cleanup = () => {
                retryChannel.unbind('pusher:subscription_succeeded', onSuccess);
                retryChannel.unbind('pusher:subscription_error', onError);
                clearTimeout(timeoutId);
              };
              const onSuccess = () => {
                if (settled) return;
                settled = true;
                cleanup();
                resolve(true);
              };
              const onError = (data: unknown) => {
                if (settled) return;
                settled = true;
                cleanup();
                const errorStr = JSON.stringify(data);
                if (isPermanentSubscribeFailure(errorStr)) {
                  // Reject to bail the loop entirely — permanent means
                  // retrying won't help.
                  reject(new Error(`Pusher auth failed: ${errorStr}`));
                } else {
                  resolve(false);
                }
              };
              // Treat a hung subscribe (no success/error event within the
              // timeout window) as a transient failure so the next backoff
              // tick gets a chance to recover. Without this, the per-attempt
              // promise can stay pending forever and starve the channel's
              // retry slot.
              const timeoutId = setTimeout(() => {
                if (settled) return;
                settled = true;
                cleanup();
                console.warn(
                  `[Pusher] Subscribe attempt ${
                    attempt + 1
                  } timed out for channel=${channel} after ${SUBSCRIBE_ATTEMPT_TIMEOUT_MS}ms`
                );
                resolve(false);
              }, SUBSCRIBE_ATTEMPT_TIMEOUT_MS);
              retryChannel.bind('pusher:subscription_succeeded', onSuccess);
              retryChannel.bind('pusher:subscription_error', onError);
            });

            if (succeeded) {
              const retryWord = attempt === 0 ? 'retry' : 'retries';
              console.debug(
                `[Pusher] Recovered channel=${channel} after ${
                  attempt + 1
                } ${retryWord}`
              );
              return;
            }

            const isLast = attempt === SUBSCRIBE_RETRY_DELAYS_MS.length - 1;
            const total = SUBSCRIBE_RETRY_DELAYS_MS.length;
            const suffix = isLast ? ' — giving up' : '';
            const message = `[Pusher] Subscribe retry ${
              attempt + 1
            }/${total} failed for channel=${channel}${suffix}`;
            if (isLast) {
              console.error(message);
              addDatadogError(new Error(message), {
                context: 'pusher-subscribe-retry-exhausted',
                channel,
              });
            } else {
              console.warn(message);
            }
          }
        } catch (error) {
          if (isPermanentSubscribeFailure(String(error))) {
            console.error(
              `[Pusher] Permanent failure subscribing to channel=${channel}; not retrying`,
              error
            );
            addDatadogError(error, {
              context: 'pusher-subscribe-retry-permanent',
              channel,
            });
            return;
          }
          console.error(
            `[Pusher] Unexpected error in retry loop for channel=${channel}`,
            error
          );
          addDatadogError(error, {
            context: 'pusher-subscribe-retry-unexpected',
            channel,
          });
        } finally {
          channelsBeingRetried.delete(channel);
        }
      })();
    }
  );
}

function useSubscribe() {
  const trpcContext = trpc.useContext();
  return useCallback(
    async (client: Pusher) => {
      const channels =
        await trpcContext.client.notifications.channels.get.query();

      channels.forEach((channel) => {
        subscribeOneWithRetry(client, channel);
      });

      return client;
    },
    [trpcContext.client.notifications.channels.get]
  );
}

function usePusherChannelsClient() {
  const { getAccessTokenSilently } = useRoundtableAuth0();
  const subscribeToChannels = useSubscribe();

  if (config.appEnv !== 'production') {
    Pusher.logToConsole = config.pusher.debug;
  }

  return useCallback(async () => {
    if (clientPromise) {
      return await clientPromise;
    }

    clientPromise = getAccessTokenSilently().then(makeClient);

    // On failure, allow retry by clearing the module-scope promise.
    clientPromise.catch(() => {
      clientPromise = null;
    });

    const client = await clientPromise;
    await subscribeToChannels(client);

    return client;
  }, [getAccessTokenSilently, subscribeToChannels]);
}

export function useSubscribeToPusherChannels() {
  const getClient = usePusherChannelsClient();
  const subscribeToChannels = useSubscribe();
  return useCallback(async () => {
    const client = await getClient();
    await subscribeToChannels(client);
  }, [getClient, subscribeToChannels]);
}

export function useDisconnectPushersChannels() {
  const getClient = usePusherChannelsClient();
  return async () => {
    const client = await getClient();
    // Stop the liveness heartbeat before disconnecting so it doesn't
    // race to reconnect during the teardown.
    stopHeartbeat?.();
    stopHeartbeat = null;
    // Remove the document/window liveness handlers from the previous
    // session — they captured the (now-disconnected) client by closure.
    stopLivenessListeners?.();
    // Bump the generation so any in-flight retry IIFE bails on its next
    // iteration instead of calling client.subscribe() on the dead client.
    clientGeneration += 1;
    // Clear any in-flight per-channel retry loops that captured the
    // old client by closure.
    clearChannelsBeingRetried();
    client?.disconnect();
    // Remove the promise so we can reconnect later.
    clientPromise = null;
    // Allow liveness listeners to be re-attached for the next session.
    livenessListenersAttached = false;
  };
}

export function useGetPusherChannelsClient() {
  return usePusherChannelsClient();
}
